/**
 * The pipeline's Metrics tab (docs/ux/metrics.md): four headline numbers
 * (median days in status, the DNA rate, rounds per report, cases past Beam's
 * report mark) with the counts
 * they rest on, the per-status stays, the appointment outcomes and the
 * rounds distribution, computed on the server from the transition log. No
 * chart library: the numbers are the content and the bars a reading aid.
 */

import { cn } from 'cn';
import type { ReactNode } from 'react';
import type { Metrics } from '@/app/types';
import { Legend, LegendItem } from '@/components/shell/Legend';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Skeleton } from '@/components/ui/skeleton';
import { Panel } from '@/features/matter/panels';
import { longDate, timeOfDay } from '@/lib/format';

const NOT_ENOUGH = 'Not enough data';

function StatCard({ eyebrow, value, footing, slot, tone }: { eyebrow: string; value: string | null; footing: string; slot: string; tone?: 'error' }) {
  const id = `stat-${slot}`;
  return (
    <section aria-labelledby={id} data-slot="stat-card" data-stat={slot} className="flex flex-col gap-1 rounded-xl border border-beam-line bg-beam-paper px-5 py-4">
      <h2 id={id} className="eyebrow text-beam-text-muted">{eyebrow}</h2>
      {value === null ? <p className="pt-1 text-sm text-beam-text-muted" data-slot="stat-value">{NOT_ENOUGH}</p> : <p className={cn('font-heading tabular text-[28px] leading-tight font-medium md:text-[32px]', tone === 'error' && 'text-beam-error')} data-slot="stat-value">{value}</p>}
      <p className="text-[13px] text-beam-text-muted" data-slot="stat-footing">{footing}</p>
    </section>
  );
}

function BarList({ rows, slot }: { rows: Array<{ key: string; label: ReactNode; value: number | null; unit: string; note?: string; sr: string }>; slot: string }) {
  const max = Math.max(0, ...rows.map((r) => r.value ?? 0));
  return (
    <ul className="flex flex-col gap-2" data-slot={slot}>
      {rows.map((r) => (
        <li key={r.key} className="flex flex-col gap-1 text-sm md:grid md:grid-cols-[minmax(0,220px)_minmax(0,1fr)_auto] md:items-center md:gap-3">
          <span className="sr-only">{r.sr}</span>
          <span aria-hidden="true" className="md:truncate">{r.label}</span>
          <span aria-hidden="true" className="flex items-center gap-3 md:contents">
            <span className="h-2 flex-1 rounded-full bg-beam-cream-deep md:flex-none"><span className="block h-2 rounded-full bg-beam-signature/60" style={{ width: r.value && max > 0 ? `${Math.max(2, (r.value / max) * 100)}%` : '0%' }} /></span>
            <span className="tabular whitespace-nowrap text-right">
              {r.value === null ? <span className="text-beam-text-muted">{NOT_ENOUGH}</span> : <span className="font-medium">{r.value} {r.unit}</span>}
              {r.note ? <span className="ml-2 text-[13px] text-beam-text-muted">{r.note}</span> : null}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export function MetricsTab({ metrics, isPending, isError, onRetry }: { metrics: Metrics | undefined; isPending: boolean; isError: boolean; onRetry: () => void }) {
  if (isError && !metrics) return <ErrorPanel className="mt-3" message="The metrics could not be computed." onRetry={onRetry} />;
  if (isPending || !metrics) {
    return (
      <div className="mt-3 flex flex-col gap-3" aria-busy="true" aria-label="Loading metrics">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[112px] rounded-xl" />)}</div>
        <div className="grid gap-3 lg:grid-cols-[2fr_1fr]"><Skeleton className="h-72 rounded-xl" /><Skeleton className="h-72 rounded-xl" /></div>
      </div>
    );
  }
  if (metrics.transitions === 0) return <EmptyState className="mt-6" title="No transitions recorded yet." description="Metrics are computed from the transition log as cases move." />;

  const m = metrics;
  const dwellRows = m.dwell.filter((r) => r.completed > 0 || r.open > 0).map((r) => ({
    key: String(r.status),
    label: <><span className="tabular font-medium">{r.status}</span> {r.label}</>,
    value: r.medianDays,
    unit: r.medianDays === 1 ? 'day' : 'days',
    note: `${plural(r.completed, 'stay', 'stays')}${r.open > 0 ? `, ${r.open} now` : ''}`,
    sr: `${r.status} ${r.label}: ${r.medianDays === null ? NOT_ENOUGH.toLowerCase() : `median ${r.medianDays} days`}, ${plural(r.completed, 'completed stay', 'completed stays')}${r.open > 0 ? `, ${r.open} in status now` : ''}`,
  }));
  const outcomes = [
    { key: 'attended', label: 'Attended', value: m.appointments.attended },
    { key: 'dna', label: 'Did not attend', value: m.appointments.dna },
    { key: 'cancelled', label: 'Cancelled', value: m.appointments.cancelled },
    { key: 'late', label: 'Late cancellations', value: m.appointments.lateCancellations },
    { key: 'repeat', label: 'Repeat DNA', value: m.appointments.repeatDna },
  ].map((o) => ({ ...o, unit: '', sr: `${o.label}: ${o.value}` }));
  const rounds = [
    { key: 'one', label: '1 round', value: m.rounds.distribution.one },
    { key: 'two', label: '2 rounds', value: m.rounds.distribution.two },
    { key: 'three', label: '3 or more', value: m.rounds.distribution.threeOrMore },
  ].map((r) => ({ ...r, unit: r.value === 1 ? 'report' : 'reports', sr: `${r.label}: ${plural(r.value, 'report', 'reports')}` }));

  return (
    <div className="mt-3 flex flex-col gap-3" data-slot="metrics">
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <StatCard slot="dwell" eyebrow="Median days in status" value={m.dwellOverall.medianDays === null ? null : `${m.dwellOverall.medianDays} days`} footing={`across ${plural(m.dwellOverall.completed, 'completed stay', 'completed stays')}`} />
        <StatCard slot="dna" eyebrow="DNA rate" value={m.appointments.dnaRatePercent === null ? null : `${m.appointments.dnaRatePercent}%`} footing={`${m.appointments.dna} of ${plural(m.appointments.withOutcome, 'appointment with an outcome', 'appointments with an outcome')}`} />
        <StatCard slot="rounds" eyebrow="Rounds per report" value={m.rounds.medianRounds === null ? null : `${m.rounds.medianRounds} ${m.rounds.medianRounds === 1 ? 'round' : 'rounds'}`} footing={`median across ${plural(m.rounds.reports, 'report at QA Complete', 'reports at QA Complete')}`} />
        <StatCard
          slot="mark"
          eyebrow={`Past the ${m.reportMark.days}-day mark`}
          value={`${m.reportMark.openPast} ${m.reportMark.openPast === 1 ? 'case' : 'cases'}`}
          tone={m.reportMark.openPast > 0 ? 'error' : undefined}
          footing={`${m.reportMark.breachedThisMonth} breached this month; ${m.reportMark.medianDaysToRelease === null ? NOT_ENOUGH.toLowerCase() : `median ${m.reportMark.medianDaysToRelease} days`} from appointment to release across ${plural(m.reportMark.released, 'released report', 'released reports')}`}
        />
      </div>
      <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
        <Panel title="Days in status">
          <BarList rows={dwellRows} slot="dwell-list" />
        </Panel>
        <div className="flex flex-col gap-3">
          <Panel title="Appointment outcomes"><BarList rows={outcomes} slot="outcomes-list" /></Panel>
          <Panel title="Rounds per report"><BarList rows={rounds} slot="rounds-list" /></Panel>
        </div>
      </div>
      <p className="text-[13px] text-beam-text-muted" data-slot="metrics-footing">
        From {plural(m.transitions, 'transition', 'transitions')} across {plural(m.matters, 'case', 'cases')}{m.since ? `, since ${longDate(m.since)}` : ''}. {m.released} released, {m.closed} closed. Computed at {timeOfDay(m.generatedAt)}.
      </p>
      <Legend>
        <LegendItem term={<span className="font-medium">Median days in status</span>}>a stay runs from the move into a status to the next move out; the current stay counts as "now", not in the median.</LegendItem>
        <LegendItem term={<span className="font-medium">DNA rate</span>}>did not attend, as a share of appointments with an outcome (attended plus did not attend).</LegendItem>
        <LegendItem term={<span className="font-medium">Rounds per report</span>}>a round is a doctor's submission or resubmission to QA, counted on reports that reached 9 QA Complete.</LegendItem>
        <LegendItem term={<span className="font-medium">Past the {m.reportMark.days}-day mark</span>}>attended more than {m.reportMark.days} days ago with the report not yet released; a case breaches on the day the mark passes, released or not.</LegendItem>
        <LegendItem term={<span className="font-medium">Not enough data</span>}>fewer than two samples; the number appears as cases move.</LegendItem>
      </Legend>
    </div>
  );
}
